export const popupRules = Object.freeze({
  firstPageMs: 9000,
  repeatPageMs: 9000,
  exitIntentMs: 9000,
  scrollDepth: 0.30,
  activityWindowMs: 60000,
  sessionIdleMs: 30 * 60 * 1000,
  impressionMs: 24 * 60 * 60 * 1000,
  dismissalMs: 7 * 24 * 60 * 60 * 1000,
  customIntentMs: 30 * 24 * 60 * 60 * 1000,
});

const keys = Object.freeze({
  optOut: 'minicubez_custom_car_popup_opt_out_v1',
  legacyDismissed: 'minicubez_custom_car_popup_dismissed_v1',
  preferences: 'minicubez_custom_car_popup_preferences_v2',
  session: 'minicubez_custom_car_popup_session_v2',
});

export function popupPageReason(context) {
  if (context.designMode) return 'theme-editor';
  if (Number(context.cartCount) > 0 || ['cart', 'checkout'].includes(context.pageType)) return 'shopping';
  const identity = [context.path, context.templateSuffix, context.contentName].join(' ').toLowerCase();
  if (/custom[-_ ]?(car|bike)|your-custom-car|custom order/.test(identity)) return 'custom-order';
  if (/\b(bikes?|motorcycles?|motorbikes?|motorcycle-sets)\b/.test(identity)) return 'bike-interest';
  if (!['index', 'collection', 'product'].includes(context.pageType)) return 'non-catalogue';
  return 'eligible';
}

export function popupDecision({ context, preferences = {}, session = {}, now, elapsedMs = 0,
  depth = 0, scrollReached = depth >= popupRules.scrollDepth, lastInteraction = 0, visible = true, busy = false, signal = 'engagement' }) {
  const pageReason = popupPageReason(context);
  if (pageReason !== 'eligible') return pageReason;
  if (preferences.optOut) return 'opt-out';
  if (session.shown || session.dismissed) return 'session-limit';
  if (session.journey) return 'shopping-journey';
  const cooldowns = [['customIntentAt', popupRules.customIntentMs], ['dismissedAt', popupRules.dismissalMs],
    ['shownAt', popupRules.impressionMs]];
  for (const [key, duration] of cooldowns) {
    if (Number(preferences[key]) > 0 && now - Number(preferences[key]) < duration) return 'cooldown';
  }
  if (!visible || busy) return 'busy';
  if (!lastInteraction || now - lastInteraction > popupRules.activityWindowMs) return 'idle';
  const repeatVisit = Number(session.pageViews) >= 2;
  const minimumMs = signal === 'exit' ? popupRules.exitIntentMs : repeatVisit ? popupRules.repeatPageMs : popupRules.firstPageMs;
  if (elapsedMs < minimumMs || !scrollReached) return 'not-engaged';
  return 'show';
}

export function createPopupSession(previous, now) {
  if (!previous || !Number.isFinite(previous.lastSeen) || now - previous.lastSeen >= popupRules.sessionIdleMs) {
    return { pageViews: 0, lastSeen: now, shown: false, dismissed: false, journey: false };
  }
  return { ...previous, lastSeen: now };
}

export function safePopupStorage(storageProvider, key, value) {
  try {
    const storage = storageProvider();
    if (value === undefined) return storage.getItem(key);
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
    return true;
  } catch {
    return value === undefined ? null : false;
  }
}

export function initializePopup(doc = document, win = window) {
  const configElement = doc.querySelector('[data-mccp-config]');
  if (!configElement || configElement.dataset.ready === 'true') return;
  configElement.dataset.ready = 'true';
  const context = JSON.parse(configElement.textContent);
  const popup = doc.querySelector('[data-mccp]');
  const local = () => win.localStorage;
  const storageSession = () => win.sessionStorage;
  const readJson = (provider, key) => {
    try { return JSON.parse(safePopupStorage(provider, key) || '{}'); } catch { return {}; }
  };
  let preferences = readJson(local, keys.preferences);
  let session = createPopupSession(readJson(storageSession, keys.session), Date.now());
  const saveSession = () => safePopupStorage(storageSession, keys.session, JSON.stringify(session));
  const savePreferences = () => safePopupStorage(local, keys.preferences, JSON.stringify(preferences));
  const pageReason = popupPageReason(context);
  if (['shopping', 'custom-order', 'bike-interest'].includes(pageReason)) {
    session.journey = true;
    saveSession();
    if (pageReason === 'custom-order') {
      preferences.customIntentAt = Date.now();
      savePreferences();
    }
  }
  if (!popup || pageReason !== 'eligible') return;
  session.pageViews += 1;
  session.dismissed ||= safePopupStorage(storageSession, keys.legacyDismissed) === 'true';
  saveSession();
  const dialog = popup.querySelector('.mccp__dialog');
  const optOut = popup.querySelector('[data-mccp-opt-out]');
  let previouslyFocused = null;
  const initializedAt = Date.now();
  const navigationStart = Number(win.performance?.timeOrigin);
  const arrivedAt = Number.isFinite(navigationStart) && navigationStart > 0 && navigationStart <= initializedAt
    ? navigationStart : initializedAt;
  let depth = 0;
  let scrollReached = false;
  const scrollAnchor = context.pageType === 'index' ? doc.querySelector('minicubez-video-reviews .mvr__heading') : null;
  const updateScrollProgress = () => {
    const available = doc.documentElement.scrollHeight - win.innerHeight;
    if (available > 0) depth = Math.max(depth, Math.min(1, win.scrollY / available));
    if (scrollReached) return;
    const hasAnchor = scrollAnchor?.isConnected && scrollAnchor.getClientRects().length > 0;
    scrollReached = hasAnchor
      ? win.scrollY > 0 && scrollAnchor.getBoundingClientRect().bottom <= win.innerHeight
      : depth >= popupRules.scrollDepth;
  };
  let lastInteraction = initializedAt;
  let timer;
  let arrivalTimer;
  let finished = false;
  const refreshPreferences = () => {
    preferences = { ...preferences, ...readJson(local, keys.preferences),
      optOut: safePopupStorage(local, keys.optOut) === 'true' };
  };
  const busy = () => {
    if (doc.activeElement?.matches('input, textarea, select, [contenteditable="true"], iframe')) return true;
    if (doc.body.matches('.overflow-hidden, .overflow-hidden-mobile, .promo-popup--open, .promo-popup-p--open')) return true;
    const blockers = doc.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], details[open] .search-modal, .drawer.active, predictive-search[open], .promo-popup.is-active, .promo-popup-p.is-active, .menu-drawer-container[open]');
    return Array.from(blockers).some(element => {
      if (popup.contains(element) || element.closest('[hidden], [aria-hidden="true"], details:not([open])') || !element.getClientRects().length) return false;
      const style = win.getComputedStyle(element);
      return style.visibility !== 'hidden' && style.visibility !== 'collapse' && style.opacity !== '0';
    });
  };
  const decision = signal => {
    updateScrollProgress();
    refreshPreferences();
    if (Number(doc.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden="true"]')?.textContent) > 0) {
      session.journey = true;
      saveSession();
    }
    const now = Date.now();
    return popupDecision({ context, preferences, session, now, elapsedMs: Math.max(0, now - arrivedAt), depth, scrollReached,
      lastInteraction, visible: doc.visibilityState === 'visible', busy: busy(), signal });
  };
  const stop = () => { finished = true; win.clearInterval(timer); win.clearTimeout(arrivalTimer); };
  const close = (dismissed = true) => {
    if (!popup.classList.contains('is-open')) return;
    if (dismissed) {
      session.dismissed = true;
      preferences.dismissedAt = Date.now();
      safePopupStorage(storageSession, keys.legacyDismissed, 'true');
      saveSession();
      savePreferences();
    }
    stop();
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    doc.documentElement.classList.remove('mccp-lock');
    if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    win.setTimeout(() => {
      popup.hidden = true;
    }, win.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280);
  };
  const show = signal => {
    if (finished) return;
    const reason = decision(signal);
    popup.dataset.mccpState = reason;
    if (reason !== 'show') return;
    session.shown = true;
    preferences.shownAt = Date.now();
    saveSession();
    savePreferences();
    previouslyFocused = doc.activeElement;
    optOut.checked = preferences.optOut;
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    doc.documentElement.classList.add('mccp-lock');
    stop();
    win.requestAnimationFrame(() => {
      popup.classList.add('is-open');
      win.setTimeout(() => popup.querySelector('.mccp__close').focus(), 40);
    });
    doc.dispatchEvent(new CustomEvent('mc:custom-offer', { detail: { action: 'shown', trigger: signal } }));
  };
  const interact = () => {
    lastInteraction = Date.now();
    session.lastSeen = lastInteraction;
  };
  const scroll = () => {
    interact();
    updateScrollProgress();
    show('engagement');
  };
  for (const eventName of ['pointerdown', 'keydown', 'touchstart']) doc.addEventListener(eventName, interact, { passive: true });
  win.addEventListener('scroll', scroll, { passive: true });
  win.addEventListener('pagehide', saveSession);
  doc.addEventListener('cart:open', () => { session.journey = true; saveSession(); stop(); });
  doc.addEventListener('cart-update', () => { session.journey = true; saveSession(); stop(); });
  doc.addEventListener('submit', event => {
    if (event.target.matches('form[action*="/cart/add"], form[action*="/checkout"]')) {
      session.journey = true;
      saveSession();
      stop();
    }
  }, true);
  doc.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const destination = new URL(link.href, win.location.href);
    if (destination.origin === win.location.origin && /\/(cart|checkout|checkouts)(\/|$)/.test(destination.pathname)) {
      session.journey = true;
      saveSession();
      stop();
    }
  });
  for (const button of popup.querySelectorAll('[data-mccp-close]')) button.addEventListener('click', () => close());
  optOut.addEventListener('change', () => {
    preferences.optOut = optOut.checked;
    safePopupStorage(local, keys.optOut, optOut.checked ? 'true' : null);
    savePreferences();
  });
  popup.querySelector('[data-mccp-cta]').addEventListener('click', () => {
    preferences.customIntentAt = Date.now();
    session.journey = true;
    savePreferences();
    saveSession();
    close(false);
  });
  doc.addEventListener('keydown', event => {
    if (!popup.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const elements = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(element => element.getClientRects().length > 0);
    const first = elements[0], last = elements.at(-1);
    if (event.shiftKey && (doc.activeElement === first || doc.activeElement === dialog)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  doc.addEventListener('mouseout', event => {
    if (!event.relatedTarget && event.clientY <= 8 && win.matchMedia('(hover: hover) and (pointer: fine)').matches) show('exit');
  });
  timer = win.setInterval(() => {
    show('engagement');
    if (['opt-out', 'cooldown', 'session-limit', 'shopping-journey'].includes(popup.dataset.mccpState)) stop();
  }, 1000);
  const minimumMs = Number(session.pageViews) >= 2 ? popupRules.repeatPageMs : popupRules.firstPageMs;
  arrivalTimer = win.setTimeout(() => show('engagement'), Math.max(0, minimumMs - (Date.now() - arrivedAt)));
}

if (typeof document !== 'undefined') initializePopup();
